import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { X, Bell, ArrowLeftRight, CheckCheck, Circle, Inbox, ShieldAlert, Check, AlertTriangle, CircleDashed, MapPinOff } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  exchange_received:  { icon: ArrowLeftRight, tone: "text-purple-400", label: "Scambio ricevuto" },
  exchange_accepted:  { icon: ArrowLeftRight, tone: "text-emerald-400", label: "Scambio accettato" },
  exchange_rejected:  { icon: ArrowLeftRight, tone: "text-rose-400", label: "Scambio rifiutato" },
  exchange_assignment_update: { icon: ArrowLeftRight, tone: "text-amber-400", label: "Driver/Veicolo aggiornato" },
  exchange_cancelled: { icon: ArrowLeftRight, tone: "text-slate-400", label: "Scambio annullato" },
  exchange_completed: { icon: ArrowLeftRight, tone: "text-sky-400", label: "Scambio completato" },
  driver_assignment:  { icon: Inbox, tone: "text-amber-400", label: "Assegnazione autista" },
  doc_expiry:         { icon: ShieldAlert, tone: "text-rose-400", label: "Scadenza documento" },
  vehicle_report:     { icon: ShieldAlert, tone: "text-amber-400", label: "Controllo veicolo" },
  gps_silent:         { icon: MapPinOff, tone: "text-rose-400", label: "GPS assente" },
  pickup_risk:        { icon: AlertTriangle, tone: "text-amber-400", label: "Possibile ritardo" },
};

const CHECK_META = {
  ok:         { icon: Check, cls: "bg-emerald-500 text-black", label: "Tutto OK" },
  anomaly:    { icon: AlertTriangle, cls: "bg-amber-400 text-black", label: "Segnalazione" },
  incomplete: { icon: CircleDashed, cls: "bg-sky-400/20 text-sky-300 border border-sky-400/60", label: "Incompleto" },
};

const fmtDT = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
const FILTERS = [["all", "Tutte"], ["unread", "Non lette"], ["read", "Storico lette"]];

function NotifIcon({ n }) {
  if (n.type === "vehicle_check") {
    const M = CHECK_META[n.outcome] || CHECK_META.ok;
    const I = M.icon;
    return <div className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${M.cls}`} data-testid={`notif-outcome-${n.outcome}`}><I size={16} strokeWidth={3} /></div>;
  }
  const M = TYPE_META[n.type] || { icon: Bell, tone: "text-slate-400" };
  const I = M.icon;
  return <div className={`mt-0.5 w-8 flex justify-center ${M.tone}`}><I size={16} /></div>;
}

export default function NotificationsPanel({ onClose, onNavigate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "unread" ? { unread_only: true } : filter === "read" ? { read_only: true } : {};
      const r = await api.get("/notifications", { params });
      setItems(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const markRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markAllRead = async () => {
    try {
      const r = await api.post("/notifications/read-all");
      toast.success(`${r.data?.updated || 0} notifiche segnate come lette`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleClick = async (n) => {
    if (!n.read) await markRead(n.id);
    if (n.type === "doc_expiry" || n.type === "vehicle_report" || n.type === "vehicle_check" || n.type === "gps_silent" || n.type === "pickup_risk") {
      onNavigate(n.link || "/scadenze");
    } else if (String(n.type || "").startsWith("exchange_")) {
      onNavigate("/scambio-servizi");
    } else if (n.booking_id) {
      onNavigate("/prenotazioni");
    }
  };

  return (
    <div className="fixed inset-0 z-[60]" data-testid="notifications-panel">
      <div className="absolute inset-0 bg-black/60" onClick={onClose}/>
      <div className="absolute right-0 top-0 h-full w-full sm:w-[440px] bg-[#0A0907] border-l border-amber-500/25 shadow-2xl flex flex-col pt-safe">
        <div className="px-5 py-4 border-b border-amber-500/15 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell size={18} className="text-amber-400"/>
            <div className="font-display text-xl font-extrabold text-white">Notifiche</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="close-notif-panel"><X size={20}/></button>
        </div>
        <div className="px-5 py-2 flex items-center justify-between gap-2 border-b border-amber-500/10">
          <div className="flex gap-1 rounded-lg border border-slate-800 bg-slate-900/60 p-0.5 text-xs">
            {FILTERS.map(([k, l]) => (
              <button key={k} onClick={()=>setFilter(k)} data-testid={`notif-filter-${k}`}
                className={`px-2.5 py-1 rounded transition-colors ${filter === k ? "bg-amber-500 text-slate-950 font-bold" : "text-slate-400 hover:text-slate-200"}`}>{l}</button>
            ))}
          </div>
          <button onClick={markAllRead} className="text-xs text-amber-300/80 hover:text-amber-300 inline-flex items-center gap-1" data-testid="mark-all-read"><CheckCheck size={12}/> Segna tutte lette</button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-10 text-center text-slate-500 text-sm">Caricamento...</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm" data-testid="notif-empty">Nessuna notifica</div>
          ) : (
            <div className="divide-y divide-slate-800/60">
              {items.map(n => (
                <button key={n.id} onClick={()=>handleClick(n)} data-testid={`notif-item-${n.id}`}
                  className={`w-full text-left px-5 py-3.5 hover:bg-amber-500/5 transition-colors ${!n.read ? "bg-amber-500/[0.06]" : ""}`}>
                  <div className="flex items-start gap-3">
                    <NotifIcon n={n} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-bold text-white truncate">{n.title || TYPE_META[n.type]?.label || n.type}</div>
                        {!n.read && <Circle size={7} className="fill-rose-500 text-rose-500 flex-shrink-0" data-testid="notif-unread-dot"/>}
                        <div className="ml-auto text-[10px] text-slate-500 font-mono-num flex-shrink-0">{fmtDT(n.created_at)}</div>
                      </div>
                      {n.type === "vehicle_check" ? (
                        <>
                          <div className="text-xs text-slate-300 mt-0.5 truncate">{n.vehicle_plate} · {n.driver_name}</div>
                          <div className="text-xs text-slate-500 truncate">{n.summary}</div>
                        </>
                      ) : <div className="text-xs text-slate-400 mt-0.5 line-clamp-2">{n.message}</div>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
